import type { ChangeEvent } from "react";
import { useState } from "react";
import { REVIEW_ERROR_TEXT } from "~/components/blocks/product/common/blocks/product-tab-contents/review/const";
import { isOpenReviewModalVar } from "~/components/stores/detail-product/store";
import { openDialog, closeDialog } from "~/components/stores/dialog/store";
import { openTextOnlyToast } from "~/components/stores/toast/store";
import { useQueryString } from "~/hooks/use-query-string";
import { FetchUserReviewInfoDocument, usePostVideoReviewMutation } from "~/lib/graphql";
import { useAuth } from "~/provider/auth";
import { getDevice } from "~/util/get-device";

const defaultState = {
  value: "",
  error: false,
  errorText: "",
};

type UsePostReviewArgs = {
  reviewerNickName: string;
};

export const usePostReview = (args: UsePostReviewArgs) => {
  const { reviewerNickName } = args;

  const { isLoggedIn } = useAuth();
  const { season } = useQueryString();

  const [addPostVideoReview, { loading: isLoadingAddPostVideoReview }] = usePostVideoReviewMutation({
    errorPolicy: "all",
    refetchQueries: [{ query: FetchUserReviewInfoDocument, variables: { seasonId: season, isLoggedIn } }],
  });

  const [starScore, setStarScore] = useState(0);
  const [nickName, setNickName] = useState(defaultState);
  const [title, setTitle] = useState(defaultState);
  const [textareaValue, setTextareaValue] = useState(defaultState);
  const [checkedArray, setCheckedArray] = useState([""]);

  const addReviewHandler = async () => {
    if (isLoadingAddPostVideoReview) return;

    const { data } = await addPostVideoReview({
      variables: {
        input: {
          seasonId: season,
          reviewerName: reviewerNickName === "" ? nickName.value : undefined,
          title: title.value,
          point: starScore,
          hasSpoiler: checkedArray.includes("spoiler"),
          comment: textareaValue.value,
          device: getDevice(),
        },
      },
    });

    if (data?.postVideoReview === undefined) return;

    // error codeが含まれている場合の処理
    if (data.postVideoReview.error?.code) {
      openDialog({
        title: "レビューの投稿に失敗しました",
        description: REVIEW_ERROR_TEXT[data.postVideoReview.error?.code],
        mainButtonLabelText: "閉じる",
        mainButtonName: "閉じる",
        isCancelButton: false,
        onMainButtonAction: closeDialog,
      });
      return;
    }

    if (data.postVideoReview.mutationId) {
      isOpenReviewModalVar(false);
      openTextOnlyToast("ADD_REVIEW");
      setStarScore(0);
      setNickName(defaultState);
      setTitle(defaultState);
      setTextareaValue(defaultState);
    } else {
      openDialog({
        title: "レビューの投稿に失敗しました",
        description: `現在アクセスが集中しております\n時間を置いて再度アクセスしてください`,
        mainButtonLabelText: "閉じる",
        mainButtonName: "閉じる",
        isCancelButton: false,
        onMainButtonAction: closeDialog,
      });
    }
  };

  const onStarIconClicked = (idx: number) => {
    setStarScore(idx);
  };

  const onChangeInputNickNameValue = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.currentTarget;

    if (/^[\s\u3000]|[\s\u3000]$/g.test(value)) {
      setNickName({
        ...nickName,
        value,
        error: true,
        errorText: "ご指定のニックネームは利用できません。",
      });
    } else if (value.length > 12) {
      setNickName({
        ...nickName,
        value: value,
        error: true,
        errorText: "12文字以内で入力してください。",
      });
    } else {
      setNickName({
        ...nickName,
        value: value,
        error: false,
        errorText: "",
      });
    }
  };

  const onChangeInputTitleValue = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.currentTarget;

    if (value.length > 32) {
      setTitle({
        ...title,
        value: value,
        error: true,
        errorText: "32文字以内で入力してください。",
      });
    } else if (value.length >= 1 && textareaValue.value.length === 0) {
      setTextareaValue({
        ...textareaValue,
        error: true,
        errorText: "30文字以上入力してください。",
      });
      setTitle({
        ...title,
        value: value,
        error: false,
        errorText: "",
      });
    } else if (value.length === 0 && textareaValue.value.length >= 1) {
      setTitle({
        ...title,
        value: value,
        error: true,
        errorText: "1文字以上入力してください",
      });
    } else if (value.length === 0 && textareaValue.value.length === 0) {
      setTitle({
        ...title,
        value: value,
        error: false,
        errorText: "",
      });
      setTextareaValue({
        ...textareaValue,
        error: false,
        errorText: "",
      });
    } else {
      setTitle({
        ...title,
        value: value,
        error: false,
        errorText: "",
      });
    }
  };

  const onChangeTextareaValue = (currentValue: string) => {
    if (currentValue.length !== 0 && currentValue.length < 30) {
      setTextareaValue({
        ...textareaValue,
        value: currentValue,
        error: true,
        errorText: "30文字以上入力してください。",
      });
      if (title.value.length === 0 && currentValue.length >= 1) {
        setTitle({
          ...title,
          error: true,
          errorText: "1文字以上入力してください",
        });
      }
    } else if (title.value.length >= 1 && currentValue.length === 0) {
      setTextareaValue({
        ...textareaValue,
        value: currentValue,
        error: true,
        errorText: "30文字以上入力してください。",
      });
    } else if (title.value.length === 0 && currentValue.length === 0) {
      setTitle({
        ...title,
        error: false,
        errorText: "",
      });
      setTextareaValue({
        ...textareaValue,
        value: currentValue,
        error: false,
        errorText: "",
      });
    } else if (currentValue.length > 1000) {
      setTextareaValue({
        ...textareaValue,
        value: currentValue,
        error: true,
        errorText: "1000文字以内で入力してください。",
      });
    } else {
      setTextareaValue({
        ...textareaValue,
        value: currentValue,
        error: false,
        errorText: "",
      });
    }
  };

  const onClickNickNameClearButton = () => {
    setNickName({ ...nickName, value: "" });
  };

  const onClickTitleClearButton = () => {
    setTitle({ ...title, value: "" });
    if (textareaValue.value.length >= 1) {
      setTitle({
        ...title,
        value: "",
        error: true,
        errorText: "1文字以上入力してください",
      });
    } else if (title.value.length === 0 && textareaValue.value.length === 0) {
      setTitle({
        ...title,
        error: false,
        errorText: "",
      });
      setTextareaValue({
        ...textareaValue,
        error: false,
        errorText: "",
      });
    }
  };

  const onChangeCheckbox = (checkedValue: string) => {
    if (checkedArray.includes(checkedValue)) {
      setCheckedArray(checkedArray.filter((item) => item !== checkedValue));
    } else {
      setCheckedArray([...checkedArray, checkedValue]);
    }
  };

  /** 星が0だったら非活性 */
  const isStar = starScore === 0;

  /** ニックネームが0または12文字以上だったら非活性（初回投稿の場合のみバリデーションをかける） */
  const isNickName = reviewerNickName === "" ? nickName.value.length === 0 || nickName.error : false;

  /** 投稿するボタンが非活性になる条件 */
  const isPostReviewBtnDisabled =
    isStar ||
    isNickName ||
    (title.value.length >= 1 && textareaValue.value.length === 0) ||
    (title.value.length === 0 && textareaValue.value.length >= 1) ||
    title.error ||
    textareaValue.error;

  return {
    checkedArray,
    nickName,
    starScore,
    title,
    textareaValue,
    isPostReviewBtnDisabled,
    addReviewHandler,
    onStarIconClicked,
    onChangeCheckbox,
    onChangeInputNickNameValue,
    onChangeInputTitleValue,
    onChangeTextareaValue,
    onClickNickNameClearButton,
    onClickTitleClearButton,
  };
};
